// procurementRoutes.tsx — the root-scope RFQ routes (PROC-ROUTES-BUILDER-01).
//
// These three used to be <Route> literals inline in App.tsx, which meant the
// ONLY way to test their ordering was to hand-mirror them in a test file. A
// mirror proves nothing: deleting /procurement/rfqs/new from App.tsx left the
// whole suite green, because the mirror still declared it. The routes are
// therefore built here, and App.tsx and the tests consume the SAME builder —
// so deleting a route below now turns the suite red.
//
// Same build/render split as routes/groupRouteAliases.tsx: the list form lets a
// test assert on paths and their ORDER, the render form drops straight into the
// <Routes> tree in App.tsx.
//
// Scope: root/tenant `/procurement/rfqs*` only. The group twins live in
// groupRouteAliases.tsx (B29 Lane D block) and the RFQ pages themselves are
// scope-aware via pages/Procurement/rfqScope.ts.

import { ReactNode } from 'react'
import { Route, Navigate } from 'react-router-dom'

// The lazy components live in their own module so this one exports nothing but
// builders — see the note in procurementPages.ts (fast refresh / lint).
import { RFQCreatePage, RFQDetailPage, RFQsPage } from './procurementPages'

interface ProcurementRoute {
  path: string
  element: ReactNode
}

/**
 * The root RFQ routes, in declaration order.
 *
 * `/procurement/rfqs/new` is declared BEFORE `/procurement/rfqs/:rfqId` (the
 * /dms/transmittals precedent, App.tsx:651-652). React Router v7 ranks static
 * segments above dynamic ones regardless of order, so the ordering is
 * documentation rather than load-bearing — but if `new` ever goes missing,
 * `:rfqId` swallows it, `Number("new")` is NaN and RFQDetailPage renders
 * "Invalid RFQ ID" instead of a create form. That was RFQ-CREATE-ROUTE-01.
 */
export function buildProcurementRouteList(isAuthenticated: boolean): ProcurementRoute[] {
  const protect = (Page: React.ComponentType): ReactNode =>
    isAuthenticated ? <Page /> : <Navigate to="/login" replace />

  return [
    { path: '/procurement/rfqs', element: protect(RFQsPage) },
    { path: '/procurement/rfqs/new', element: protect(RFQCreatePage) },
    { path: '/procurement/rfqs/:rfqId', element: protect(RFQDetailPage) },
  ]
}

/** Render the root RFQ routes as <Route> elements for App.tsx's <Routes>. */
export function buildProcurementRoutes(isAuthenticated: boolean): ReactNode {
  return buildProcurementRouteList(isAuthenticated).map(({ path, element }) => (
    <Route key={path} path={path} element={element} />
  ))
}
